/**
 * ReferenceWrapper - Shared wrapper for reference block visuals
 *
 * Applies display options (scale, opacity, etc.) to referenced content.
 * Overrides render mode via BlockRenderProvider for animated vs static content.
 */

import { useBlockRenderContext, BlockRenderProvider } from '@/components/blocks'
import './reference-wrapper.css'
import React from 'react'

/**
 * Display options for reference wrappers.
 * Based on DisplayOptions from blocks.types but with explicit properties
 * to avoid issues with optional schema types.
 */
export interface ReferenceDisplayOptions {
	// Visual display options
	showLabels?: boolean
	showNotes?: boolean
	showGrid?: boolean
	showPlayerNames?: boolean
	// Scale and sizing
	scale?: number
	opacity?: number
	zoom?: number
	// Color options
	highlightColor?: string
	backgroundColor?: string
	borderColor?: string
	// Layout options
	fitMode?: 'contain' | 'cover' | 'fill' | 'none'
	alignment?: 'start' | 'center' | 'end'
	padding?: number
	margin?: number
	// Animation options (planned for presentation playback)
	animated?: boolean
	animationDuration?: number
	animationDelay?: number
}

interface ReferenceWrapperProps {
	displayOptions: ReferenceDisplayOptions
	aspectRatio?: number
	children: React.ReactNode
}

export function ReferenceWrapper({
	displayOptions,
	aspectRatio,
	children,
}: ReferenceWrapperProps) {
	const parentContext = useBlockRenderContext()

	// Determine inner mode - animated for presentation playback, static otherwise
	const innerMode = displayOptions?.animated ? 'animated' : 'static'

	// Container styles from display options
	const containerStyle: React.CSSProperties = {
		aspectRatio: displayOptions?.fitMode ? undefined : aspectRatio,
		opacity: displayOptions?.opacity ?? 1,
		backgroundColor: displayOptions?.backgroundColor,
		borderColor: displayOptions?.borderColor,
		borderWidth: displayOptions?.borderColor ? 1 : 0,
		borderStyle: displayOptions?.borderColor ? 'solid' : 'none',
		padding: displayOptions?.padding,
		overflow: 'hidden',
	}

	// Apply scale via transform
	if (displayOptions?.scale && displayOptions.scale !== 1) {
		containerStyle.transform = `scale(${displayOptions.scale})`
		containerStyle.transformOrigin = 'top left'
	}

	return (
		<div className="reference-wrapper" style={containerStyle}>
			<BlockRenderProvider value={{ ...parentContext, mode: innerMode }}>
				{children}
			</BlockRenderProvider>
		</div>
	)
}
